/**
 * @expFlag 3promo-enabled-public-animated-trend-modal
 * @ticket CLOUDWEB-20130
 * @file
 */
import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import animatedTrendVariantA from 'img/public/animatedTrendModal/a.gif';
import animatedTrendVariantB from 'img/public/animatedTrendModal/b.gif';
import animatedTrendVariantC from 'img/public/animatedTrendModal/c.gif';
import animatedTrendVariantD from 'img/public/animatedTrendModal/d.gif';
import React from 'react';
import { IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { PROMO_ID, publicAnimatedTrendModal } from 'reactApp/appHelpers/featuresHelpers/features/3promoPublicAnimatedTrendModal';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { store as reduxStore } from 'reactApp/store';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/Mobile/PublicAnimatedTrendModal/PublicAnimatedTrendModal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const publicAnimatedTrendModalCreator: ModelCreator = ({ models, render, stores }) => {
    let preloadingImages: string[] = [];
    if (publicAnimatedTrendModal && publicAnimatedTrendModal.type !== 'e') {
        switch (publicAnimatedTrendModal.type) {
            case 'a':
                preloadingImages = [animatedTrendVariantA];
                break;
            case 'b':
                preloadingImages = [animatedTrendVariantB];
                break;
            case 'c':
                preloadingImages = [animatedTrendVariantC];
                break;
            case 'd':
                preloadingImages = [animatedTrendVariantD];
                break;
            default:
                preloadingImages = [];
        }
    }

    return new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showDelay: 5000,
            templateData: { image: preloadingImages[0] },
            ignoreTestOnRedraw: true,
            markAsCompleted: false,
            ignoreTimeWhenShowed: true,
            ignorePromoShowInterval: true,
            preloadingImages,
            test: (resolve, reject) => {
                if (!IS_MOBILE_BROWSER) {
                    reject();
                    return;
                }
                if (sessionStorage.getItem(PROMO_ID)) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                // Проверяем, что мы в эксперименте publicBenefitsOfAuthorization
                if (!publicAnimatedTrendModal || publicAnimatedTrendModal.type === 'e') {
                    reject();
                    return;
                }

                // Показываем только в public
                if (storage !== EStorageType.public) {
                    reject();
                    return;
                }

                resolve();
            },
            onClose() {
                sessionStorage.setItem(PROMO_ID, 'true');
            },
            onHide() {
                sessionStorage.setItem(PROMO_ID, 'true');
            },
            onShow(model) {
                model.reset();
            },
        })
    );
};
